import React, { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Medal, Star, TrendingUp } from 'lucide-react';
import { getLeaderboard } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations';

const rankIcons = {
  1: <Medal className="h-4 w-4 text-amber-500" />,
  2: <Medal className="h-4 w-4 text-slate-400" />,
  3: <Medal className="h-4 w-4 text-amber-600" />,
};

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
}

export function ActivitySidebar({ user }) {
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    loadLeaderboard();
  }, []);

  async function loadLeaderboard() {
    try {
      const res = await getLeaderboard({ period: 'month' });
      setLeaderboard(res.data);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <aside className="space-y-6">
      {/* Top Givers */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-5 w-5 text-green-500" />
            {t('topGivers', language)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">{t('loading', language)}</p>
          ) : leaderboard?.top_givers?.length > 0 ? (
            <ul className="space-y-3">
              {leaderboard.top_givers.slice(0, 5).map((entry, index) => (
                <li key={entry.id} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center">
                    {rankIcons[index + 1] || (
                      <span className="text-sm text-muted-foreground">{index + 1}</span>
                    )}
                  </span>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={entry.avatar} alt={entry.name} />
                    <AvatarFallback className="text-xs">
                      {getInitials(entry.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">{entry.name}</p>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    {entry.recognition_count}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('noDataYet', language)}</p>
          )}
        </CardContent>
      </Card>

      {/* Top Receivers */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Star className="h-5 w-5 text-amber-500" />
            {t('mostRecognized', language)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">{t('loading', language)}</p>
          ) : leaderboard?.top_receivers?.length > 0 ? (
            <ul className="space-y-3">
              {leaderboard.top_receivers.slice(0, 5).map((entry, index) => (
                <li key={entry.id} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center">
                    {rankIcons[index + 1] || (
                      <span className="text-sm text-muted-foreground">{index + 1}</span>
                    )}
                  </span>
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={entry.avatar} alt={entry.name} />
                    <AvatarFallback className="text-xs">
                      {getInitials(entry.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium">{entry.name}</p>
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    {entry.recognition_count}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('noDataYet', language)}</p>
          )}
        </CardContent>
      </Card>
    </aside>
  );
}

export default ActivitySidebar;
